
import React, { FC, useState, useRef, useEffect } from 'react';
import { Message, Announcement, UserProfile, Kudo, StudentConnection } from '../types';
import { useTranslate } from '../hooks/useTranslate';
import { Avatar, AttachmentButton, VoiceInput, DropdownMenu } from './common';
import { ListenButton } from './ListenButton';
import { ai, SYSTEM_INSTRUCTION_TRANSLATOR, translateContent } from '../lib/gemini';
import { LANGUAGES, KUDO_BADGES, KUDO_CATEGORY_COLORS, PRINCIPAL_AWARD_COLOR, REACTIONS, TEACHER_KUDO_BADGES } from '../lib/constants';

type MessageBubbleProps = {
    message: Message;
    isOwn: boolean;
    onTranslate: (text: string, targetLang: string) => Promise<string>;
    setViewingImageUrl: (url: string | null) => void;
    allUsers: UserProfile[];
    t: (key: string, reps?: Record<string, string>) => string;
    user: UserProfile;
    onUpdateMessage?: (messageId: string, newText: string, targetLanguage: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    targetLanguage?: string;
};

const MessageBubble: FC<MessageBubbleProps> = ({ message, isOwn, onTranslate, setViewingImageUrl, allUsers, t, user, onUpdateMessage, onDeleteMessage, targetLanguage }) => {
    const [translatedText, setTranslatedText] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [isLoadingTranslation, setIsLoadingTranslation] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(message.originalText || message.text);
    
    const userProfile = allUsers.find(u => u.id === message.senderId);
    const currentUserLanguage = user.language || 'en';
    const sourceLanguage = message.originalLanguage || (userProfile?.language || 'en');
    const originalContent = message.originalText || message.text;

    // Translation is needed if receiver language differs from message origin
    const needsTranslation = currentUserLanguage !== sourceLanguage;

    useEffect(() => {
        let isMounted = true;
        
        // Auto-translate if viewer's language differs from sender's original language
        if (needsTranslation && originalContent) {
            // If the message.text was already translated into this exact language, use it directly
            if (message.text && message.text !== originalContent && targetLanguage === currentUserLanguage) {
                setTranslatedText(message.text);
            } else {
                setIsLoadingTranslation(true);
                onTranslate(originalContent, currentUserLanguage)
                    .then(translated => {
                        if (isMounted) {
                            setTranslatedText(translated);
                            setIsLoadingTranslation(false);
                        }
                    })
                    .catch(err => {
                        console.error("Auto-translation error:", err);
                        if (isMounted) setIsLoadingTranslation(false);
                    });
            }
        } else {
            // Same language as sender: show original text
            setTranslatedText(null);
        }

        return () => {
            isMounted = false;
        };
    }, [currentUserLanguage, sourceLanguage, message.id, originalContent, needsTranslation]);

    const handleToggleTranslation = async () => {
        if (!translatedText && originalContent) {
            setIsLoadingTranslation(true);
            try {
                const translation = await onTranslate(originalContent, currentUserLanguage);
                setTranslatedText(translation);
            } catch (e) {
                console.error("Manual translation error:", e);
            } finally {
                setIsLoadingTranslation(false);
            }
        }
        setShowOriginal(!showOriginal);
    };
    
    const handleSaveEdit = () => {
        if (onUpdateMessage && targetLanguage) {
            onUpdateMessage(message.id, editText, targetLanguage);
        }
        setIsEditing(false);
    };

    // Determine which text to show
    let textToShow = originalContent;
    if (!showOriginal && (translatedText || (needsTranslation && message.text && message.text !== originalContent))) {
        textToShow = translatedText || message.text;
    }

    const showToggle = (needsTranslation && originalContent) || (translatedText !== null) || (message.text !== message.originalText && Boolean(message.originalText));
    
    const isValidDate = message.timestamp instanceof Date && !isNaN(message.timestamp.getTime());

    return (
        <div id={`msg-${message.id}`} className={`flex items-end gap-2 my-2 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            {!isOwn && (
                 <Avatar photoUrl={userProfile?.photoUrl} name={message.senderName} size="sm" />
            )}
            <div className={`p-3 rounded-2xl max-w-xs md:max-w-md ${isOwn ? 'bg-indigo-500 text-white rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none border'}`}>
                {!isOwn && <p className="text-xs font-bold text-indigo-500 mb-1">{message.senderName}</p>}
                
                {message.imageUrl && (
                    <img
                        src={message.imageUrl}
                        alt="attachment"
                        className="rounded-lg mb-2 cursor-pointer"
                        onClick={() => setViewingImageUrl(message.imageUrl!)}
                    />
                )}
                {isEditing ? (
                    <div className="space-y-2">
                        <textarea value={editText} onChange={e => setEditText(e.target.value)} className="w-full p-2 rounded bg-indigo-400 text-white text-sm" rows={3}/>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setIsEditing(false)} className="text-xs font-bold text-indigo-200">Cancel</button>
                            <button onClick={handleSaveEdit} className="text-xs font-bold text-white">Save</button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <p className="text-sm whitespace-pre-wrap">{textToShow}</p>
                        {isLoadingTranslation && !translatedText && (
                            <span className="inline-block text-[11px] opacity-75 animate-pulse mt-0.5">
                                ({t('processing_translation') || 'Translating...'})
                            </span>
                        )}
                    </div>
                )}
                
                <div className="flex justify-between items-end mt-2">
                    <div className="flex-1 flex flex-wrap items-center gap-2">
                        {showToggle && !isEditing && (
                            <button 
                                onClick={handleToggleTranslation} 
                                className={`text-xs ${isOwn ? 'text-indigo-200 hover:text-white' : 'text-indigo-600 hover:text-indigo-800'} font-bold transition-colors flex items-center gap-1`}
                            >
                                <span className="material-icons-round text-[13px]">translate</span>
                                {isLoadingTranslation ? (t('processing') || 'Translating...') : (showOriginal ? (t('see_translation') || 'See Translation') : (t('see_original') || 'See Original'))}
                            </button>
                        )}
                        {!isEditing && textToShow && (
                            <ListenButton
                                id={`chat-msg-${message.id}`}
                                text={textToShow}
                                language={showOriginal ? sourceLanguage : currentUserLanguage}
                                variant={isOwn ? 'bubble-own' : 'bubble-other'}
                                label={t('listen') || 'Listen'}
                                stopLabel={t('stop') || 'Stop'}
                            />
                        )}
                    </div>
                    <div className="flex items-center gap-1 text-right flex-shrink-0">
                        {message.isEdited && <span className={`text-xs ${isOwn ? 'text-indigo-200' : 'text-gray-400'}`}>(edited)</span>}
                        <p className={`text-xs ${isOwn ? 'text-indigo-200' : 'text-gray-400'}`}>
                            {isValidDate ? message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                        </p>
                        {isOwn && onUpdateMessage && onDeleteMessage && !isEditing && (
                             <div className="relative -mr-2">
                                <DropdownMenu onEdit={() => setIsEditing(true)} onDelete={() => onDeleteMessage(message.id)} />
                             </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

type AnnouncementBubbleProps = {
    announcement: Announcement;
    setViewingImageUrl: (url: string | null) => void;
    t: (key: string, reps?: Record<string, string>) => string;
    onEdit?: (announcement: Announcement) => void;
    onDelete?: (announcementId: string) => void;
    canManage: boolean;
    userLanguage: string;
};
const AnnouncementBubble: FC<AnnouncementBubbleProps> = ({ announcement, setViewingImageUrl, t, onEdit, onDelete, canManage, userLanguage }) => {
    const [translatedText, setTranslatedText] = useState<string | null>(null);
    const [translatedTitle, setTranslatedTitle] = useState<string | null>(null);
    const [isTranslated, setIsTranslated] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);

    useEffect(() => {
        // Reset translation state if user language changes
        setTranslatedText(null);
        setTranslatedTitle(null);
        setIsTranslated(false);
    }, [userLanguage, announcement.id, announcement.text, announcement.title]);

    const handleTranslate = async () => {
        if (!translatedText) {
            setIsTranslating(true);
            try {
                const textResult = await translateContent(announcement.text, userLanguage);
                const titleResult = await translateContent(announcement.title, userLanguage);
                setTranslatedText(textResult);
                setTranslatedTitle(titleResult);
                setIsTranslated(true);
            } catch (e) {
                console.error("Announcement translation error:", e);
            } finally {
                setIsTranslating(false);
            }
        } else {
            setIsTranslated(!isTranslated);
        }
    };

    const isValidDate = announcement.timestamp instanceof Date && !isNaN(announcement.timestamp.getTime());
    return (
        <div className="my-4">
            <div className="text-center text-xs text-gray-400 mb-1">
                {isValidDate ? announcement.timestamp.toLocaleDateString() : '...'}
            </div>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg shadow-sm">
                <p className="font-bold text-sm text-yellow-800 mb-1">{t('announcement_by', { senderName: announcement.senderName })}</p>
                
                {/* Title */}
                <h3 className="font-bold text-lg text-yellow-900 mb-1">{isTranslated && translatedTitle ? translatedTitle : announcement.title}</h3>

                {announcement.imageUrls && announcement.imageUrls.length > 0 && (
                     <img
                        src={announcement.imageUrls[0]}
                        alt="announcement attachment"
                        className="rounded-lg my-2 cursor-pointer border border-yellow-200"
                        onClick={() => setViewingImageUrl(announcement.imageUrls![0])}
                    />
                )}
                <p className="text-sm text-yellow-800 mt-1 whitespace-pre-wrap">{isTranslated && translatedText ? translatedText : announcement.text}</p>
                <div className="flex justify-between items-center mt-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <button 
                            onClick={handleTranslate} 
                            disabled={isTranslating}
                            className="text-xs font-bold text-yellow-700 hover:text-yellow-900 flex items-center gap-1"
                        >
                            <span className="material-icons-round text-[13px]">translate</span>
                            {isTranslating ? (t('processing') || 'Translating...') : (isTranslated ? (t('show_original') || 'Show Original') : (t('translate') || 'Translate'))}
                        </button>
                        <ListenButton
                            id={`chat-ann-${announcement.id}`}
                            text={`${isTranslated && translatedTitle ? translatedTitle : announcement.title}. ${isTranslated && translatedText ? translatedText : announcement.text}`}
                            language={isTranslated ? userLanguage : 'en'}
                            variant="announcement"
                            label={t('listen') || 'Listen'}
                            stopLabel={t('stop') || 'Stop'}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        {announcement.isEdited && <span className="text-xs text-yellow-600">(edited)</span>}
                        <p className="text-xs text-yellow-600">
                            {isValidDate ? announcement.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                        </p>
                        {canManage && onEdit && onDelete && (
                            <div className="relative">
                                <DropdownMenu onEdit={() => onEdit(announcement)} onDelete={() => onDelete(announcement.id)} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const KudoBubble: FC<{ kudo: Kudo, t: (key: string, reps?: any) => string; studentName?: string; userLanguage?: string }> = ({ kudo, t, studentName, userLanguage = 'en' }) => {
    const [translatedNote, setTranslatedNote] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);

    useEffect(() => {
        setTranslatedNote(null);
        setShowOriginal(false);
    }, [userLanguage, kudo.id, kudo.note]);

    const handleTranslateNote = async () => {
        if (!kudo.note) return;
        if (!translatedNote) {
            setIsTranslating(true);
            try {
                const result = await translateContent(kudo.note, userLanguage);
                setTranslatedNote(result);
            } catch (err) {
                console.error("Kudo note translation error:", err);
            } finally {
                setIsTranslating(false);
            }
        }
        setShowOriginal(!showOriginal);
    };

    // Determine badge set based on recipient type (Teacher vs Student)
    const isTeacherKudo = !!kudo.teacherId;
    const badgeSet = isTeacherKudo ? TEACHER_KUDO_BADGES : KUDO_BADGES;
    
    const badge = kudo.badgeId ? badgeSet.find(b => b.id === kudo.badgeId) : null;
    const reaction = kudo.reactionType ? REACTIONS.find(r => r.id === kudo.reactionType) : null;

    let colors = {
        text: 'text-gray-500',
        border: 'border-gray-400',
    };
    let icon = 'military_tech';
    let name = 'Kudo';

    if (badge) {
        colors = kudo.isPrincipalAward
            ? { text: PRINCIPAL_AWARD_COLOR.text, border: PRINCIPAL_AWARD_COLOR.border }
            : { text: KUDO_CATEGORY_COLORS[badge.category]?.text || colors.text, border: KUDO_CATEGORY_COLORS[badge.category]?.border || colors.border };
        icon = badge.icon;
        name = kudo.badgeName || badge.name;
    } else if (reaction) {
        colors = { text: 'text-yellow-500', border: 'border-yellow-400' };
        icon = reaction.icon;
        name = reaction.name;
    }

    const noteToShow = showOriginal ? kudo.note : (translatedNote || kudo.note);

    return (
        <div className="my-4">
             <div className="text-center text-xs text-gray-400 mb-1">
                {kudo.timestamp instanceof Date && !isNaN(kudo.timestamp.getTime()) ? kudo.timestamp.toLocaleDateString() : ''}
            </div>
            <div className={`p-4 rounded-lg bg-gradient-to-br from-yellow-50 to-amber-100 border-l-4 ${colors.border}`}>
                <div className="flex items-start gap-4">
                    <div className={`text-4xl ${colors.text}`}>
                        <span className="material-icons-round !text-5xl">{icon}</span>
                    </div>
                    <div className="flex-1">
                        {kudo.isPrincipalAward && <p className="text-xs font-bold text-amber-600 uppercase">{t('principals_award')}</p>}
                        <p className="font-bold text-gray-800">{name}</p>
                        
                        {isTeacherKudo ? (
                            <p className="text-sm font-medium text-gray-800 mt-1">
                                {t('awarded_to')} <span className="font-bold">{kudo.teacherName || 'Teacher'}</span>
                            </p>
                        ) : (
                            studentName && (
                                <p className="text-sm font-medium text-gray-800 mt-1">
                                    {t('awarded_to')} <span className="font-bold">{studentName}</span>
                                </p>
                            )
                        )}
                        
                        <p className="text-sm text-gray-600">{t('awarded_by')} {kudo.senderName}</p>
                        {kudo.note && (
                            <div className="mt-2 bg-white/60 p-2.5 rounded-md border border-amber-200/50">
                                <p className="text-sm text-gray-700 italic">"{noteToShow}"</p>
                                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                    <button 
                                        onClick={handleTranslateNote}
                                        disabled={isTranslating}
                                        className="text-xs font-bold text-amber-800 hover:text-amber-900 flex items-center gap-1 transition-colors"
                                    >
                                        <span className="material-icons-round text-[12px]">translate</span>
                                        {isTranslating ? (t('processing') || 'Translating...') : (showOriginal ? (t('see_translation') || 'See Translation') : (t('see_original') || 'See Original'))}
                                    </button>
                                    <ListenButton
                                        id={`chat-kudo-${kudo.id}`}
                                        text={noteToShow}
                                        language={showOriginal ? 'en' : userLanguage}
                                        variant="kudo"
                                        label={t('listen') || 'Listen'}
                                        stopLabel={t('stop') || 'Stop'}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


const MessageInputForm = ({ 
    onSendMessage, 
    user, 
    targetLanguage, 
    readOnly, 
    t, 
    initialMessage,
    isDirectChat = false,
    isDirectChatDisabled = false,
    dailyLimit,
    dailyCount = 0,
    allowAttachments = false
}: {
    onSendMessage: (text: string, originalText: string, imageUrl?: string) => void;
    user: UserProfile;
    targetLanguage: string;
    readOnly: boolean;
    t: (key: string, reps?: Record<string, string>) => string;
    initialMessage?: string;
    isDirectChat?: boolean;
    isDirectChatDisabled?: boolean;
    dailyLimit?: number;
    dailyCount?: number;
    allowAttachments?: boolean;
}) => {
    const [inputText, setInputText] = useState(initialMessage || '');
    const [imageAttachment, setImageAttachment] = useState<string>();
    const [isProcessing, setIsProcessing] = useState(false);
    
    useEffect(() => {
        if(initialMessage) {
            setInputText(initialMessage);
        }
    }, [initialMessage])

    const handleTranslate = async (text: string, lang: string) => {
        return await translateContent(text, lang);
    };

    const isLimitReached = typeof dailyLimit === 'number' && dailyLimit > 0 && dailyCount >= dailyLimit;
    const remainingMessages = typeof dailyLimit === 'number' && dailyLimit > 0 ? Math.max(0, dailyLimit - dailyCount) : null;
    const MAX_CHAT_LENGTH = 1000;
    const charCount = inputText.length;
    const isNearLimit = charCount >= 850;
    const isOverLimit = charCount > MAX_CHAT_LENGTH;
    
    const handleSend = async () => {
        if (isLimitReached || isProcessing) return;
        const originalText = inputText.trim();
        if (!originalText && !imageAttachment) return;
        if (originalText.length > MAX_CHAT_LENGTH) {
            alert(`Message exceeds the maximum limit of ${MAX_CHAT_LENGTH} characters. Please make it more concise.`);
            return;
        }

        setIsProcessing(true);
        let textToSend = originalText;
        if (user.language !== targetLanguage && originalText) {
            textToSend = await handleTranslate(originalText, targetLanguage);
        }
        
        onSendMessage(textToSend, originalText, imageAttachment);
        setInputText('');
        setImageAttachment(undefined);
        setIsProcessing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (readOnly) {
        return <div className="p-4 text-center text-sm text-gray-500 bg-gray-100">This conversation is archived and read-only.</div>;
    }

    if (user.role === 'parent' && !isDirectChat) {
        return (
            <div className="p-3 bg-amber-50 border-t border-amber-200 text-center flex items-center justify-center gap-2 text-amber-800 text-xs font-semibold">
                <span className="material-icons-round text-base text-amber-600">campaign</span>
                <span>Class Group Chat is a broadcast channel from school teachers and principal. Use the <strong>Direct Chat</strong> tab to message teachers.</span>
            </div>
        );
    }

    if (user.role === 'parent' && isDirectChatDisabled) {
        return (
            <div className="p-4 bg-red-50 border-t border-red-200 text-center text-red-700 text-sm flex items-center justify-center gap-2">
                <span className="material-icons-round text-red-500">block</span>
                <span>Direct messaging is currently disabled for your account. Please contact the school office for assistance.</span>
            </div>
        );
    }

    if (user.role === 'parent' && isLimitReached) {
        return (
            <div className="p-3 bg-orange-50 border-t border-orange-200 text-center text-orange-800 text-xs font-semibold flex items-center justify-center gap-2">
                <span className="material-icons-round text-orange-500 text-base">hourglass_top</span>
                <span>Daily message limit reached ({dailyLimit}/{dailyLimit} messages sent today). Please check back tomorrow.</span>
            </div>
        );
    }

    const canAttach = user.role !== 'parent' || allowAttachments || isDirectChat;

    return (
        <div className="p-2 bg-white border-t border-gray-200">
            {remainingMessages !== null && user.role === 'parent' && (
                <div className="px-2 pb-1.5 flex justify-between items-center text-[11px] text-gray-500 font-medium">
                    <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        Direct Chat with Teachers & Principal
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${remainingMessages <= 1 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                        {remainingMessages} {remainingMessages === 1 ? 'message' : 'messages'} remaining today
                    </span>
                </div>
            )}
            {imageAttachment && (
                <div className="relative w-24 h-24 p-2">
                    <img src={imageAttachment} alt="preview" className="w-full h-full object-cover rounded-lg" />
                     <button onClick={() => setImageAttachment(undefined)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"><span className="material-icons-round text-sm">close</span></button>
                </div>
            )}
            <div className="flex items-end gap-2">
                {canAttach && (
                    <AttachmentButton onImageSelect={setImageAttachment} />
                )}
                <div className="flex-1 flex flex-col">
                    <textarea
                        rows={1}
                        value={inputText}
                        onChange={e => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('type_a_message')}
                        className={`w-full p-3 bg-gray-100 rounded-2xl resize-none border-none outline-none text-sm ${isOverLimit ? 'ring-2 ring-red-500' : ''}`}
                        disabled={isProcessing}
                        maxLength={MAX_CHAT_LENGTH}
                    />
                    {(isNearLimit || charCount > 0) && (
                        <div className="flex justify-end px-2 pt-0.5">
                            <span className={`text-[10px] font-medium tracking-tight ${
                                isOverLimit 
                                    ? 'text-red-600 font-bold' 
                                    : isNearLimit 
                                    ? 'text-amber-600 font-semibold' 
                                    : 'text-gray-400'
                            }`}>
                                {charCount}/{MAX_CHAT_LENGTH}
                            </span>
                        </div>
                    )}
                </div>
                 <VoiceInput 
                    onTranscriptReceived={(transcript) => setInputText(prev => {
                        const combined = `${prev} ${transcript}`.trim();
                        return combined.slice(0, MAX_CHAT_LENGTH);
                    })}
                    onProcessingChange={setIsProcessing}
                 />
                <button 
                    onClick={handleSend} 
                    disabled={isProcessing || (!inputText.trim() && !imageAttachment) || isOverLimit} 
                    className="p-3 bg-indigo-600 text-white rounded-full disabled:opacity-50 hover:bg-indigo-700 transition-colors flex-shrink-0 mb-1"
                >
                    <span className="material-icons-round">send</span>
                </button>
            </div>
        </div>
    );
};

export const ChatWindow: FC<{
  messages: Message[];
  announcements: Announcement[];
  kudos: Kudo[];
  onSendMessage: (text: string, originalText: string, imageUrl?: string) => void;
  user: UserProfile;
  targetLanguage: string;
  allUsers: UserProfile[];
  setViewingImageUrl: (url: string | null) => void;
  readOnly?: boolean;
  initialMessage?: string;
  onUpdateMessage?: (messageId: string, newText: string, targetLanguage: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onEditAnnouncement?: (announcement: Announcement) => void;
  onDeleteAnnouncement?: (announcementId: string) => void;
  students: StudentConnection[];
  isDirectChat?: boolean;
  isDirectChatDisabled?: boolean;
  dailyLimit?: number;
  dailyCount?: number;
  allowAttachments?: boolean;
  onMarkMessagesRead?: (messageIds: string[]) => void;
}> = ({ 
    messages, 
    announcements, 
    kudos, 
    onSendMessage, 
    user, 
    targetLanguage, 
    allUsers, 
    setViewingImageUrl, 
    readOnly = false, 
    initialMessage, 
    onUpdateMessage, 
    onDeleteMessage, 
    onEditAnnouncement, 
    onDeleteAnnouncement, 
    students,
    isDirectChat = false,
    isDirectChatDisabled = false,
    dailyLimit,
    dailyCount = 0,
    allowAttachments = false,
    onMarkMessagesRead
}) => {
    const t = useTranslate(user.language);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const translationCache = useRef<Record<string, string>>({});
    const [initialScrollDone, setInitialScrollDone] = useState(false);

    // Automatically mark visible unread messages as read
    useEffect(() => {
        if (!onMarkMessagesRead || messages.length === 0) return;
        const unreadIds = messages
            .filter(m => m.senderId !== user.id && !(m.readBy || []).includes(user.id))
            .map(m => m.id);
        if (unreadIds.length > 0) {
            onMarkMessagesRead(unreadIds);
        }
    }, [messages, user.id, onMarkMessagesRead]);

    const combinedList = [
        ...messages.map(m => ({ ...m, viewType: 'message' as const, date: m.timestamp })),
        ...announcements.map(a => ({ ...a, viewType: 'announcement' as const, date: a.timestamp })),
        ...kudos.map(k => ({ ...k, viewType: 'kudo' as const, date: k.timestamp }))
    ].sort((a, b) => {
        const dateA = a.date instanceof Date ? a.date.getTime() : 0;
        const dateB = b.date instanceof Date ? b.date.getTime() : 0;
        return dateA - dateB;
    });

    useEffect(() => {
        // Reset initial scroll state when the chat context changes significantly
        if (combinedList.length === 0) {
            setInitialScrollDone(false);
            return;
        }

        if (!initialScrollDone) {
            // Logic for first load: find first unread message
            const firstUnread = combinedList.find(m => m.viewType === 'message' && m.senderId !== user.id && !m.readBy?.includes(user.id));
            
            if (firstUnread) {
                const el = document.getElementById(`msg-${firstUnread.id}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setInitialScrollDone(true);
                    return;
                }
            }
            // If no unread or element not found, scroll to bottom
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            setInitialScrollDone(true);
        } else {
            // Logic for subsequent updates (new messages). 
            // We rely on the dependency array to only trigger this when `combinedList.length` changes.
            // This prevents auto-scrolling when messages are merely updated (e.g. marked as read) but not added.
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [combinedList.length, user.id]); // Removed initialScrollDone from dependency to avoid loop, logic handles it inside.

    const handleTranslate = async (text: string, lang: string) => {
        const cacheKey = `${lang}:${text}`;
        if (translationCache.current[cacheKey]) {
            return translationCache.current[cacheKey];
        }
        const translated = await translateContent(text, lang);
        translationCache.current[cacheKey] = translated;
        return translated;
    };

    return (
        <div className="flex flex-col h-full bg-gray-100">
            <div className="flex-1 overflow-y-auto p-4">
                {combinedList.map(item => {
                    switch (item.viewType) {
                        case 'message':
                            return (
                                <MessageBubble
                                    key={item.id}
                                    message={item}
                                    isOwn={item.senderId === user.id}
                                    onTranslate={handleTranslate}
                                    setViewingImageUrl={setViewingImageUrl}
                                    allUsers={allUsers}
                                    t={t}
                                    user={user}
                                    targetLanguage={targetLanguage}
                                    onUpdateMessage={readOnly ? undefined : onUpdateMessage}
                                    onDeleteMessage={readOnly ? undefined : onDeleteMessage}
                                />
                            );
                        case 'announcement':
                            return (
                                <AnnouncementBubble
                                    key={item.id}
                                    announcement={item}
                                    setViewingImageUrl={setViewingImageUrl}
                                    t={t}
                                    canManage={user.role === 'principal' && !readOnly}
                                    onEdit={readOnly ? undefined : onEditAnnouncement}
                                    onDelete={readOnly ? undefined : onDeleteAnnouncement}
                                    userLanguage={user.language}
                                />
                            );
                        case 'kudo':
                             const student = students.find(s => s.id === item.studentId);
                             return (
                                <KudoBubble
                                    key={item.id}
                                    kudo={item}
                                    t={t}
                                    studentName={student?.childName}
                                    userLanguage={user.language}
                                />
                             );
                        default:
                            return null;
                    }
                })}
                <div ref={messagesEndRef} />
            </div>
            <MessageInputForm 
              onSendMessage={onSendMessage} 
              user={user} 
              targetLanguage={targetLanguage}
              readOnly={readOnly}
              t={t}
              initialMessage={initialMessage}
              isDirectChat={isDirectChat}
              isDirectChatDisabled={isDirectChatDisabled}
              dailyLimit={dailyLimit}
              dailyCount={dailyCount}
              allowAttachments={allowAttachments}
            />
        </div>
    );
};
